import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Galleria } from "primereact/galleria";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { QuickViewResponsiveOptions } from "./quick-view-responsive";
import { useTranslation } from "react-i18next";
import { ProductNewModel } from "../../models/products/product-new-model";
import { shareProduct } from "../../utils/product-share.utils";
import { Tooltip } from "primereact/tooltip";
import ProductService from "../../services/products/products.service";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";

const fallbackImage = process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png";

const ProductQuickView = ({
  productData,
  showQuickViewDialog,
  addToCartDialog,
  hideQuickViewDialog,
  minQuantity,
}: any) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [images, setImages] = useState<any[]>([]);
  const [productDialog, setProductDialog] = useState<ProductNewModel>(productData);
  const [quantity, setQuantity] = useState(0);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!showQuickViewDialog || !productData) return;

      setProductDialog(productData);
      setQuantity(minQuantity || 1);
      //setImages(mapProductImageData(productData.media));
      setLoadedImages({});
      setActiveImageIndex(0);
      setIsGalleryLoading(true);

      try {
        const result = await ProductService.getProductImageList(productData.id);
        const sortedMedia = [...(result || [])].sort(
          (a: any, b: any) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0)
        );
        setImages(sortedMedia);
      } catch (error) {
        console.error(error);
        setImages([]);
      } finally {
        setIsGalleryLoading(false);
      }
    };

    fetchData();
  }, [productData, minQuantity, showQuickViewDialog]);

  const markImageLoaded = (key: string) => {
    setLoadedImages((prev) => {
      if (prev[key]) {
        return prev;
      }

      return {
        ...prev,
        [key]: true,
      };
    });
  };

  const itemTemplate = (item: any) => {
    const imageKey = item.mediaUrl || item.alt || "gallery-item";
    const isLoaded = !!loadedImages[imageKey];

    return (
      <div
        style={{
          width: "100%",
          height: "320px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          background: "#f8faf8",
          borderRadius: "16px",
        }}
      >
        {!isLoaded && <Skeleton width="calc(100% - 32px)" height="calc(100% - 32px)" borderRadius="14px" />}

        <img
          src={item.mediaUrl}
          alt={item.alt || productDialog.name}
          onLoad={() => markImageLoaded(imageKey)}
          // onError={(event) => {
          //   if (event.currentTarget.dataset.fallbackApplied === "true") {
          //     markImageLoaded(imageKey);
          //     return;
          //   }

          //   event.currentTarget.dataset.fallbackApplied = "true";
          //   event.currentTarget.src = fallbackImage;
          // }}
          style={{
            maxWidth: "100%",
            maxHeight: "320px",
            objectFit: "contain",
            display: "block",
            padding: "16px",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.2s ease",
            position: isLoaded ? "relative" : "absolute",
          }}
        />
      </div>
    );
  };

  const thumbnailTemplate = (item: any) => {
    const imageKey = `thumb-${item.mediaUrl || item.alt || "gallery-thumb"}`;
    const isLoaded = !!loadedImages[imageKey];

    return (
      <div
        style={{
          width: "60px",
          height: "60px",
          position: "relative",
          borderRadius: "10px",
          overflow: "hidden",
          background: "#f4f6f4",
        }}
      >
        {!isLoaded && <Skeleton width="60px" height="60px" borderRadius="10px" />}

        <img
          src={item.mediaUrl || fallbackImage}
          alt={item.alt || productDialog.name}
          onLoad={() => markImageLoaded(imageKey)}
          onError={(event) => {
            if (event.currentTarget.dataset.fallbackApplied === "true") {
              markImageLoaded(imageKey);
              return;
            }

            event.currentTarget.dataset.fallbackApplied = "true";
            event.currentTarget.src = fallbackImage;
          }}
          style={{
            width: "60px",
            height: "60px",
            objectFit: "cover",
            display: "block",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.2s ease",
            position: isLoaded ? "relative" : "absolute",
            inset: 0,
          }}
        />
      </div>
    );
  };

  const getTaxPercent = () => {
    const tax = (productDialog as any)?.tax;
    return tax !== null && tax !== undefined && tax > 0 ? tax : 0;
  };

  const getItemCost = () => {
    const unitPrice = productDialog?.unitPrice || 0;
    return unitPrice * quantity;
  };

  const getTaxAmount = () => {
    const itemCost = getItemCost();
    const taxPercent = getTaxPercent();
    return itemCost * (taxPercent / 100);
  };

  const getTotalCost = () => {
    return getItemCost() + getTaxAmount();
  };

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const renderDescription = (description: string) => {
    if (!description) return "Premium quality product";

    const numberedPattern = /(\d+\.\s+[^0-9]+(?=\d+\.|$))/g;
    const matches = description.match(numberedPattern);

    if (matches && matches.length > 1) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {matches.map((item, index) => (
            <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <span style={{ fontWeight: "600", color: "#166534", minWidth: "20px" }}>
                {item.match(/^\d+/)?.[0]}.
              </span>
              <span style={{ fontSize: "14px", lineHeight: "1.5", color: "#6b7280" }}>
                {item.replace(/^\d+\.\s+/, "").trim()}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return description;
  };

  const handleShareProduct = () => {
    shareProduct(productDialog.id, toast);
  };

  const handlePreviousImage = () => {
    setActiveImageIndex((prevIndex) => {
      if (images.length <= 1) {
        return 0;
      }

      return prevIndex === 0 ? images.length - 1 : prevIndex - 1;
    });
  };

  const handleNextImage = () => {
    setActiveImageIndex((prevIndex) => {
      if (images.length <= 1) {
        return 0;
      }

      return prevIndex === images.length - 1 ? 0 : prevIndex + 1;
    });
  };

  const handleQuantityInputBlur = (valueStr: string) => {
    const minQty = productDialog?.minOrderQty || 1;
    const step = productDialog.orderMultiples || 1;
    let val = parseFloat(valueStr);
    
    if (isNaN(val) || val <= 0) {
      setQuantity(minQty);
      return;
    }

    if (val < minQty) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Warning',
        detail: 'Entered quantity is less than minimum quantity',
        life: 3000
      });
      setQuantity(minQty);
    } else {
      const steps = Math.round((val - minQty) / step);
      let roundedVal = minQty + Math.max(0, steps) * step;

      // Avoid floating-point precision issues (e.g. 2.4 -> 2, 2.6 -> 3)
      const getDecimalPlaces = (num: number): number => {
        const match = ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
        if (!match) return 0;
        return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
      };
      const decimals = Math.max(getDecimalPlaces(minQty), getDecimalPlaces(step));
      roundedVal = parseFloat(roundedVal.toFixed(decimals));
      
      setQuantity(roundedVal);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={showQuickViewDialog}
        className="quickview cust-dialog"
        breakpoints={{ "960px": "90vw", "641px": "95vw" }}
        modal
        onHide={hideQuickViewDialog}
        style={{ maxWidth: "900px" }}
        icons={
          <>
            <Tooltip target=".share-product-btn" position="bottom" />
            <button
              onClick={handleShareProduct}
              className="p-dialog-header-icon p-link share-product-btn"
              type="button"
              aria-label="Share product"
              data-pr-tooltip="Share Product"
              style={{
                marginRight: "0.5rem",
                marginTop: "0.5rem",
              }}
            >
              <span className="pi pi-share-alt"></span>
            </button>
          </>
        }
      >
        <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
          <div
            style={{
              flex: "0 0 45%",
              minWidth: "280px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "relative",
                borderRadius: "18px",
                overflow: "hidden",
                backgroundColor: "#ffffff",
                border: "1px solid #e7ece8",
                padding: "12px",
              }}
            >
              {isGalleryLoading ? (
                <div
                  style={{
                    width: "100%",
                    height: "360px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    background: "#f8faf8",
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Shimmer skeleton background */}
                  <Skeleton width="100%" height="100%" borderRadius="16px" style={{ position: "absolute", inset: 0 }} />

                  {/* Spinner + label overlay */}
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      className="pi pi-spin pi-spinner"
                      style={{ fontSize: "32px", color: "#166534" }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#4b5563",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Fetching images…
                    </span>
                  </div>
                </div>
              ) : images.length > 0 ? (
                <div style={{ position: "relative" }}>
                  <Galleria
                    value={images}
                    responsiveOptions={QuickViewResponsiveOptions}
                    numVisible={4}
                    item={itemTemplate}
                    thumbnail={thumbnailTemplate}
                    activeIndex={activeImageIndex}
                    onItemChange={(event) => setActiveImageIndex(event.index)}
                    showThumbnails={images.length > 1}
                    showThumbnailNavigators={false}
                    showItemNavigators={false}
                    style={{ maxWidth: "100%", margin: "auto" }}
                    showIndicators={false}
                  />

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handlePreviousImage}
                        aria-label="Previous image"
                        style={{
                          position: "absolute",
                          top: "140px",
                          left: "12px",
                          width: "40px",
                          height: "40px",
                          borderRadius: "999px",
                          border: "1px solid #d7ddd8",
                          background: "rgba(255, 255, 255, 0.95)",
                          boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                      >
                        <span className="pi pi-chevron-left" style={{ fontSize: "14px", color: "#166534" }} />
                      </button>

                      <button
                        type="button"
                        onClick={handleNextImage}
                        aria-label="Next image"
                        style={{
                          position: "absolute",
                          top: "140px",
                          right: "12px",
                          width: "40px",
                          height: "40px",
                          borderRadius: "999px",
                          border: "1px solid #d7ddd8",
                          background: "rgba(255, 255, 255, 0.95)",
                          boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                      >
                        <span className="pi pi-chevron-right" style={{ fontSize: "14px", color: "#166534" }} />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "320px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8faf8",
                    borderRadius: "16px",
                  }}
                >
                  <img
                    src={fallbackImage}
                    alt={productDialog.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      padding: "24px",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              flex: "1",
              minWidth: "280px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <h2 style={{ margin: "0", fontSize: "28px", fontWeight: "700", color: "#1f2937" }}>
              {productDialog.name}
            </h2>

            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <div
                style={{
                  backgroundColor: "#f3f5f3",
                  padding: "6px 12px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#4b5563",
                }}
              >
                {productDialog.categoryName || "Category"}
              </div>
            </div>

            <div
              style={{
                margin: "0",
                fontSize: "15px",
                lineHeight: "1.6",
                color: "#6b7280",
              }}
            >
              {renderDescription(productDialog.description || "Premium quality product")}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>
                Quantity
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  onClick={() => {
                    const minQty = productDialog?.minOrderQty || 1;
                    const currentQty = parseFloat(quantity as any) || minQty;
                    if (currentQty > minQty) {
                      const step = productDialog.orderMultiples || 1;
                      setQuantity(parseFloat((currentQty - step).toFixed(2)));
                    }
                  }}
                  disabled={quantity <= (productDialog?.minOrderQty || 1) || productDialog?.inStock === 0}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    border: "1px solid #d7ddd8",
                    background: "#ffffff",
                    cursor:
                      quantity <= (productDialog?.minOrderQty || 1) || productDialog?.inStock === 0
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#4b5563",
                    opacity: quantity <= (productDialog?.minOrderQty || 1) ? 0.5 : 1,
                  }}
                >
                  -
                </button>
                <input
                  type="text"
                  value={quantity}
                  onChange={(event) => {
                    const val = event.target.value;
                    if (val === "" || /^[0-9]*\.?[0-9]*$/.test(val)) {
                      setQuantity(val as any);
                    }
                  }}
                  onBlur={(event) => handleQuantityInputBlur(event.target.value)}
                  disabled={productDialog?.inStock === 0}
                  style={{
                    width: "60px",
                    height: "40px",
                    textAlign: "center",
                    border: "1px solid #d7ddd8",
                    borderRadius: "10px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                />
                <button
                  onClick={() => {
                    const minQty = productDialog?.minOrderQty || 1;
                    const currentQty = parseFloat(quantity as any) || minQty;
                    const step = productDialog.orderMultiples || 1;
                    setQuantity(parseFloat((currentQty + step).toFixed(2)));
                  }}
                  disabled={productDialog?.inStock === 0}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    border: "1px solid #d7ddd8",
                    background: "#ffffff",
                    cursor: productDialog?.inStock === 0 ? "not-allowed" : "pointer",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#4b5563",
                    opacity: productDialog?.inStock === 0 ? 0.5 : 1,
                  }}
                >
                  +
                </button>
              </div>
            </div>

            <div
              style={{
                border: "1px solid #e7ece8",
                borderRadius: "16px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                background: "#fbfcfb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>Item Cost</span>
                <span style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>
                  {formatINR(getItemCost())}
                </span>
              </div>
              {getTaxPercent() > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>
                    Tax ({getTaxPercent()}%)
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>
                    {formatINR(getTaxAmount())}
                  </span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937" }}>Price</span>
                <span style={{ fontSize: "24px", fontWeight: "700", color: "#166534" }}>
                  {formatINR(Math.round(getTotalCost()))}
                </span>
              </div>
            </div>

            {productDialog?.inStock > 0 ? (
              <Button
                icon="pi pi-shopping-cart"
                className="w-full"
                label={t("products_addtocart_button")}
                disabled={productData?.availabilityPending === true}
                onClick={() => {
                  if (productData?.availabilityPending || productData?.inStock === 0) return;
                  const cartStorageItems = ProductStorageHelpers.GetProductStorage() || [];
                  const alreadyInCart = cartStorageItems.some((item: any) => item.id === productDialog.id);
                  if (alreadyInCart) {
                    toast.current?.show({ severity: 'info', summary: 'Already Added', detail: 'Item already in your cart', life: 1200 });
                    if (hideQuickViewDialog) hideQuickViewDialog();
                    return;
                  } else {
                    toast.current?.show({ severity: 'success', summary: 'Added', detail: 'Item added successfully!', life: 1200 });
                  }
                  if (addToCartDialog) {
                    addToCartDialog(quantity);
                  }
                }}
                raised
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  backgroundColor: "#166534",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                }}
              />
            ) : (
              <Button
                icon="pi pi-times"
                className="w-full"
                label="Out of Stock"
                disabled
                raised
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "not-allowed",
                  opacity: 0.7,
                }}
              />
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProductQuickView;
